import { clsx } from 'clsx'
import { IconButton } from 'component/Button'
import { Space } from 'component/Space'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { FormEvent, KeyboardEvent, useRef, useState } from 'react'
import css from './SearchField.module.scss'

export const SearchField = observer(() => {
  const [focus, setFocus] = useState(false)
  const focusTimeout = useRef<TimeoutId>()
  const searchTimeout = useRef<TimeoutId>()
  const demo = useDemo()
  const { search } = demo.form
  const ref = useRef<HTMLInputElement>(null)

  useEffectOnce(() => () => {
    clearTimeout(focusTimeout.current)
    clearTimeout(searchTimeout.current)
  })

  const startSearch = useLatestCallback(() => {
    clearTimeout(searchTimeout.current)
    demo.search()
  })

  const onFocus = useLatestCallback(() => {
    clearTimeout(focusTimeout.current)
    setFocus(true)
  })

  const onBlur = useLatestCallback(() => {
    focusTimeout.current = setTimeout(() => {
      setFocus(false)
    }, 10)
  })

  const onKeyDown = useLatestCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') startSearch()
  })

  const onChange = useLatestCallback((event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    demo.form.search = value

    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      startSearch()
    }, 1000)
  })

  const onClear = useLatestCallback(() => {
    demo.form.search = ''
    ref.current?.focus()
    startSearch()
  })

  return <div className={clsx(css.field, focus && css.focus)} onFocusCapture={onFocus} onBlurCapture={onBlur}>
    <input ref={ref} className={css.input} type="text" value={search}
      placeholder={i18n('Search')} onChange={onChange} onKeyDown={onKeyDown} />
    {search ? <IconButton action={onClear}>
      <Icon size={16} name="close" secondary />
    </IconButton> : <Space width={32} height={32} center>
      <Icon size={16} name="search" secondary />
    </Space>}
  </div>
})
